import { AppBar, Box, Container, Toolbar } from '@mui/material';
import HeaderLogo from './HeaderLogo';
import HeaderMobileMenu from './HeaderMobileMenu';
import HeaderTopMenu from './HeaderTopMenu';
import HeaderMenu from './HeaderMenu';
import styles from './Header.module.css';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { login, logout } from '../../services/userService';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { selectAssumedRole } from '../../app/slices/userSlice';
import { loadProfileProperties, selectWebUploaderLink } from '../../app/slices/configsSlice';
import { useEffect } from 'react';
import { redirectToWebUploader } from '../../services/WebUploaderService';
import { ADMIN_ACCESS, NON_ADMIN_ACCESS } from '../commons/AccesRoles';

const Header = () => {
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const userRole = useAppSelector(selectAssumedRole);
  const uploaderLink = useAppSelector(selectWebUploaderLink);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!uploaderLink) {
      dispatch(loadProfileProperties());
    }
  }, [dispatch, uploaderLink]);

  const handleLogout = (event: React.MouseEvent) => {
    event.preventDefault();
    logout(instance);
  };

  const handleLogin = (event: React.MouseEvent) => {
    event.preventDefault();
    login(instance);
  };

  const handleWebUploaderRedirect = () => {
    if (!!uploaderLink) {
      redirectToWebUploader(uploaderLink);
    }
  };

  const mobilePageAppAdminLinks = [
    { link: '/photoDay', label: 'PhotoDay Upload' },
    { link: '/', label: 'Find An Event' },
    { link: '/forms-reports', label: 'Forms & Reports' },
    { link: '/', label: 'Web Uploader', action: handleWebUploaderRedirect },
  ];

  const mobileNonAppAdminLinks = [
    { link: '/', label: 'Find An Event' },
    { link: '/forms-reports', label: 'Forms & Reports' },
  ];


  const pageLinks = [
    { link: '/photoDay', label: 'PhotoDay Upload' },
    { link: '/', label: 'Find An Event' },
    { link: '/forms-reports', label: 'Forms & Reports' },
  ];

  const userRoleLinks = userRole === 'AppUser' ? [] : [{ link: '/photoDay', label: 'PhotoDay Upload' }];

  mobileNonAppAdminLinks.unshift(...userRoleLinks);
  if(userRole === 'AppUser') {
    pageLinks.splice(0,1);
  }

  const userLinks = [
    { link: '/my-account', label: 'My Account' },
    { link: '/logout', label: 'Sign Out', action: handleLogout },
  ];

  const loginLink = { link: '/', label: 'Sign In', action: handleLogin };

  return (
    <Box sx={{ backgroundColor: 'background.default' }}>
      <Container maxWidth="xl" className={styles.topMenuContainer}>
        {isAuthenticated && <HeaderTopMenu links={userLinks} />}
        {!isAuthenticated && <HeaderTopMenu links={[loginLink]} />}
      </Container>
      <AppBar
        position="sticky"
        color="inherit"
        sx={{
          color: 'text.primary',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
              <HeaderLogo />
            </Box>

            <Box
              sx={{
                flexGrow: 0,
                display: { xs: 'none', md: 'flex' },
              }}
            >
              {isAuthenticated && <HeaderMenu userRole={userRole} links={pageLinks} />}
            </Box>

            <Box sx={{ flexGrow: 0, display: { xs: 'flex', md: 'none' } }}>
              {!!userRole && (ADMIN_ACCESS.includes(userRole)) && isAuthenticated && (
                <HeaderMobileMenu links={[mobilePageAppAdminLinks, userLinks]} />
              )}
              {!!userRole && (NON_ADMIN_ACCESS.includes(userRole)) && isAuthenticated && (
                <HeaderMobileMenu links={[mobileNonAppAdminLinks, userLinks]} />
              )}
              {!isAuthenticated && <HeaderMobileMenu links={[[loginLink]]} />}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
    </Box>
  );
};

export default Header;
